import React, { useState } from 'react';
import config from '../config';
import CompanyDisambiguationModal from './CompanyDisambiguationModal';
import BackButton from './BackButton';

function CompanyIntelPage({ 
  setPage, 
  companySearchName, 
  setCompanySearchName, 
  companyLoading, 
  setCompanyLoading, 
  companyIntel, 
  setCompanyIntel, 
  userUsage,
  getCpcDescription,
  getWipoDescription
}) {
  const [showDisambiguation, setShowDisambiguation] = useState(false);
  const [disambiguationMatches, setDisambiguationMatches] = useState([]);

  const handleDisambiguationSelect = (selectedCompany) => {
    setShowDisambiguation(false);
    setDisambiguationMatches([]);
    searchCompanyDirect(selectedCompany).then(() => {
      setCompanySearchName(selectedCompany);
    });
  };

  const searchCompanyDirect = async (exactName) => {
    setCompanyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(config.API_URL + "/api/company-intel/" + encodeURIComponent(exactName) + "?token=" + token);
      const data = await response.json();
      if (response.status === 429 || response.status === 403) {
        alert(data.detail || 'You have reached your daily limit');
        setCompanyLoading(false);
        return;
      }
      if (!response.ok) {
        alert(data.detail || 'Company intel search failed');
        setCompanyLoading(false);
        return;
      }
      setCompanyIntel(data);
    } catch (error) {
      console.error('Company intel error:', error);
      alert('Search failed: ' + error.message);
    }
    setCompanyLoading(false);
  };

  const handleCompanySearch = async () => {
    if (!companySearchName.trim()) {
      alert('Please enter a company name');
      return;
    }

    try {
      const disambigResponse = await fetch(config.API_URL + "/api/disambiguate-company?query=" + encodeURIComponent(companySearchName));
      const disambigData = await disambigResponse.json();
      
      if (disambigData.needs_disambiguation && disambigData.matches.length > 1) {
        setDisambiguationMatches(disambigData.matches);
        setShowDisambiguation(true);
        return;
      }
      
      const exactName = disambigData.matches.length === 1 ? disambigData.matches[0].assignee : companySearchName;
      searchCompanyDirect(exactName);
    } catch (err) {
      searchCompanyDirect(companySearchName);
    }
  };

  const getPatentUrl = (patentNumber) => {
    return "https://worldwide.espacenet.com/patent/search?q=pn%3DUS" + patentNumber;
  };

  return (
    <div style={{ minHeight: '100vh', background: 'white' }}>
      <header style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <BackButton onClick={() => setPage('search')} />
          <img src="/logo.svg" alt="PatTrak Logo" style={{ height: '32px', cursor: 'pointer' }} onClick={() => setPage('search')} />
        </div>
      </header>

      <main style={{ maxWidth: '1000px', margin: '40px auto', padding: '0 16px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1d2934', marginBottom: '8px' }}>
          Company Intelligence
        </h1>
        <p style={{ color: '#5e778a', marginBottom: '16px' }}>
          Analyze any company's patent portfolio - granted patents, pending applications, technology focus, and top inventors.
        </p>
        {userUsage?.company_intel && (
          <div style={{ fontSize: '13px', color: '#5e778a', marginBottom: '16px' }}>
            {userUsage.company_intel.limit < 9999 ? (
              <>Company intel searches: {userUsage.company_intel.remaining} of {userUsage.company_intel.limit} remaining today</>
            ) : (
              <span style={{ color: '#10b981' }}>Unlimited company intel searches</span>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginBottom: '32px' }}>
          <input
            type="text"
            value={companySearchName}
            onChange={(e) => setCompanySearchName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCompanySearch()}
            placeholder="Enter company name (e.g., Tesla, Apple, Samsung)"
            style={{ flex: 1, padding: '12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '16px' }}
          />
          <button
            onClick={handleCompanySearch}
            disabled={companyLoading}
            style={{
              background: companyLoading ? '#ccc' : '#1976d2',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '6px',
              border: 'none',
              fontSize: '16px',
              cursor: companyLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {companyLoading ? 'Analyzing...' : 'Analyze'}
          </button>
        </div>

        {companyIntel && (
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1d2934', marginBottom: '20px' }}>
              {companyIntel.company_name} - IP Portfolio
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
              <div style={{ background: '#e3f2fd', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#1976d2' }}>{companyIntel.summary?.granted_patents?.toLocaleString()}</div>
                <div style={{ fontSize: '14px', color: '#666' }}>Granted Patents</div>
              </div>
              <div style={{ background: '#fff3e0', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#e65100' }}>{companyIntel.summary?.pending_applications?.toLocaleString()}</div>
                <div style={{ fontSize: '14px', color: '#666' }}>Pending Applications</div>
              </div>
              <div style={{ background: '#e8f5e9', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#2e7d32' }}>{companyIntel.summary?.total_portfolio?.toLocaleString()}</div>
                <div style={{ fontSize: '14px', color: '#666' }}>Total Portfolio</div>
              </div>
              <div style={{ background: '#fce4ec', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#c2185b' }}>{companyIntel.summary?.times_cited?.toLocaleString()}</div>
                <div style={{ fontSize: '14px', color: '#666' }}>Times Cited</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
              <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>Technology Focus (CPC)</h3>
                {companyIntel.technology_breakdown?.map((tech, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontWeight: '500', cursor: 'help' }} title={getCpcDescription(tech.cpc_class)}>{tech.cpc_class}</span>
                    <span style={{ color: '#666' }}>{tech.count?.toLocaleString()} patents</span>
                  </div>
                ))}
              </div>

              <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>WIPO Sectors</h3>
                {companyIntel.wipo_sectors?.map((sector, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontWeight: '500', cursor: 'help' }} title={getWipoDescription(sector.sector)}>{sector.sector}</span>
                    <span style={{ color: '#666' }}>{sector.count?.toLocaleString()} patents</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '32px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>Top Inventors</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                {companyIntel.top_inventors?.map((inventor, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: 'white', borderRadius: '4px' }}>
                    <span>{inventor.name}</span>
                    <span style={{ color: '#1976d2', fontWeight: '600' }}>{inventor.patent_count} patents</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>Recent Granted Patents</h3>
                {companyIntel.recent_patents?.map((patent, idx) => (
                  <div key={idx} style={{ padding: '12px 0', borderBottom: '1px solid #eee' }}>
                    <a href={getPatentUrl(patent.patent_number)} target="_blank" rel="noopener noreferrer" style={{ fontWeight: '600', color: '#1976d2', textDecoration: 'none' }}>US{patent.patent_number}</a>
                    <div style={{ fontSize: '14px', color: '#333' }}>{patent.title}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{patent.grant_date}</div>
                  </div>
                ))}
              </div>

              <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>Recent Applications</h3>
                {companyIntel.recent_applications?.map((app, idx) => (
                  <div key={idx} style={{ padding: '12px 0', borderBottom: '1px solid #eee' }}>
                    <a href={getPatentUrl(app.pgpub_id)} target="_blank" rel="noopener noreferrer" title="Application may not yet be available on Espacenet - recently filed applications can take 18 months to publish" style={{ fontWeight: '600', color: '#e65100', textDecoration: 'none', cursor: 'help' }}>US{app.pgpub_id}</a>
                    <div style={{ fontSize: '14px', color: '#333' }}>{app.title}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{app.published_date}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
      <CompanyDisambiguationModal
        show={showDisambiguation}
        onClose={() => setShowDisambiguation(false)}
        query={companySearchName}
        matches={disambiguationMatches}
        onSelect={handleDisambiguationSelect}
        loading={false}
      />
    </div>
  );
}

export default CompanyIntelPage;
